import { useEffect, useRef, useState } from 'react';
import { FiArrowUpRight, FiChevronLeft, FiChevronRight, FiHeart, FiShoppingBag } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { apiGetBanners, apiGetSiteSection } from '../../api';
import { useCart } from '../../context/CartContext';
import { useCurrency } from '../../context/CurrencyContext';
import { getPurchaseIssue } from '../../utils/stock';
import { useToast } from '../../context/ToastContext';
import { useWishlist } from '../../context/WishlistContext';
import { getBookImage, handleBookImageError } from '../../utils';
import logger from '../../utils/logger';
import StockOverlay from '../common/StockOverlay';
import { resolveBannerLink } from '../../utils/bannerLink';
import { getAssetImage, handleAssetImageError } from '../../utils/image';

const OfferProduct = ({ book, content }) => {
  const { addToCart } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const { addToast } = useToast();
  const { formatBookPrice, getBookAmount } = useCurrency();
  const wished = isInWishlist(book.id);
  const saving = Math.max(0, getBookAmount(book, 'originalPrice') - getBookAmount(book));
  const inStock = !getPurchaseIssue({ ...book, quantity: 1 }, getBookAmount(book));

  const toggleWishlist = () => {
    if (wished) removeFromWishlist(book.id);
    else addToWishlist(book);
    addToast(wished ? content.wishlistRemoved : content.wishlistAdded, wished ? 'info' : 'success');
  };

  const addBook = () => {
    if (!inStock) return;
    addToCart(book);
    addToast(content.cartMessage.replace('{title}', book.title), 'success');
  };

  return (
    <article className="relative flex h-full min-w-0 flex-col overflow-hidden rounded-xl bg-white p-3 sm:p-4 text-black">
      <button
        type="button"
        onClick={toggleWishlist}
        aria-label={wished ? content.removeWishlistLabel : content.addWishlistLabel}
        className="absolute right-3 top-3 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-black bg-white"
      >
        <FiHeart className={wished ? 'fill-red-500 text-red-500' : 'text-black'} size={21} />
      </button>
      <Link to={`/book/${book.id}`} className="block">
        <h3 className="min-h-[3rem] pr-12 text-lg font-semibold line-clamp-2">{book.title}</h3>
        <div className="relative mt-2 aspect-[3/4] max-h-64 overflow-hidden rounded-lg bg-white">
          <img
            src={getBookImage(book.image)}
            onError={handleBookImageError}
            alt={book.title}
            className="h-full w-full object-contain transition duration-300 hover:scale-105"
            loading="lazy"
          />
          <StockOverlay stock={book.stock} />
        </div>
      </Link>
      <p className="mt-3 text-sm text-gray-500 line-clamp-1">{book.author || book.category?.name}</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {getBookAmount(book, 'originalPrice') > getBookAmount(book) && (
          <span className="text-sm text-gray-500 line-through">{formatBookPrice(book, 'originalPrice')}</span>
        )}
        <span className="text-lg font-bold">{formatBookPrice(book)}</span>
        {saving > 0 && (
          <span className="rounded-md bg-green-200 px-2 py-1 text-xs font-medium text-green-900">
            {content.saveLabel} {saving.toLocaleString()}
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={addBook}
        disabled={!inStock}
        className="mt-auto flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-yellow-400 px-2 py-3 font-medium transition hover:bg-yellow-300 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-600"
      >
        <FiShoppingBag /> {inStock ? content.addToBagLabel : 'Coming Soon'}
      </button>
    </article>
  );
};

const PromoTile = ({ banner, large = false, fallbackAlt }) => {
  const image = (
    <img
      src={getAssetImage(banner.image)}
      onError={handleAssetImageError}
      alt={banner.title || fallbackAlt}
      className="block h-auto w-full object-contain"
      loading="lazy"
    />
  );
  const className = `group flex items-center overflow-hidden rounded-xl bg-gray-100 ${large ? 'sm:col-span-2' : ''}`;

  const link = resolveBannerLink(banner.link);
  if (!link) return <div className={className}>{image}</div>;
  if (!link.internal) {
    return <a className={className} href={link.href} target="_blank" rel="noreferrer">{image}</a>;
  }
  return <Link className={className} to={link.to}>{image}</Link>;
};

const OffersSection = ({ books, category }) => {
  const [banners, setBanners] = useState([]);
  const [content, setContent] = useState(null);
  const previousRef = useRef(null);
  const nextRef = useRef(null);

  useEffect(() => {
    Promise.all([apiGetBanners('offer'), apiGetSiteSection('home_offers')])
      .then(([bannerData, contentData]) => {
        const config = contentData.data?.find((item) => item.key === 'content');
        if (!config) throw new Error('Home offers content is not configured');
        setContent(config);
        setBanners((bannerData.banners || [])
          .filter((banner) => banner.image)
          .slice(0, Math.max(0, Number(config.bannerLimit) || 0)));
      })
      .catch((error) => logger.error('Failed to fetch offer section', error));
  }, []);

  if (!books?.length || !content) return null;

  return (
    <section id="offers" className="carousel-section w-full px-3 py-3 sm:px-4 scroll-mt-24" aria-labelledby="offers-title">
      <div className="grid overflow-hidden bg-white lg:grid-cols-[minmax(0,45%)_minmax(0,55%)] lg:gap-4">
        <div className="overflow-hidden bg-red-600 p-4 sm:p-6 lg:p-8">
          <div className="mb-6 text-white">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.18em]">{content.eyebrow}</p>
              <h2 id="offers-title" className="mt-1 text-3xl font-black sm:text-5xl">{content.heading}</h2>
            </div>
            <div className="mt-4 flex items-center justify-end gap-2">
              {books.length > 1 && (
                <>
                  <button
                    ref={previousRef}
                    type="button"
                    aria-label={content.previousLabel}
                    className="carousel-arrow flex h-11 w-11 items-center justify-center rounded-full border border-white/70 bg-white text-black transition hover:bg-yellow-400"
                  >
                    <FiChevronLeft size={21} />
                  </button>
                  <button
                    ref={nextRef}
                    type="button"
                    aria-label={content.nextLabel}
                    className="carousel-arrow flex h-11 w-11 items-center justify-center rounded-full border border-white/70 bg-white text-black transition hover:bg-yellow-400"
                  >
                    <FiChevronRight size={21} />
                  </button>
                </>
              )}
            </div>
          </div>
          <Swiper
            watchOverflow
            grabCursor
            breakpointsBase="container"
            modules={[Navigation, Autoplay]}
            slidesPerView={1}
            spaceBetween={12}
            navigation={{ prevEl: previousRef.current, nextEl: nextRef.current }}
            onBeforeInit={(swiper) => {
              swiper.params.navigation.prevEl = previousRef.current;
              swiper.params.navigation.nextEl = nextRef.current;
            }}
            autoplay={content.autoplay && books.length > 1 ? {
              delay: Math.max(1000, Number(content.autoplayDelay) || 1000),
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            } : false}
            rewind
            breakpoints={{
              0: { slidesPerView: 1 },
              440: { slidesPerView: 2, spaceBetween: 16 },
            }}
            className="offers-swiper"
          >
            {books.slice(0, Math.max(0, Number(content.productLimit) || 0)).map((book) => (
              <SwiperSlide key={book.id}>
                <OfferProduct book={book} content={content} />
              </SwiperSlide>
            ))}
          </Swiper>
          <div className="mt-5 flex justify-center">
            <Link
              to={`/shop?category=${encodeURIComponent(category.name).replace(/%20/g, '+')}`}
              className="inline-flex min-h-12 min-w-36 items-center justify-between gap-4 rounded-xl bg-yellow-400 px-5 py-3 font-semibold text-black transition hover:bg-yellow-300"
            >
              <span>{content.viewAllLabel}</span>
              <FiArrowUpRight className="shrink-0" />
            </Link>
          </div>
        </div>

        {banners.length > 0 && (
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:mt-0 lg:grid-rows-2">
            {banners.slice(0, 2).map((banner) => <PromoTile key={banner.id} banner={banner} fallbackAlt={content.bannerAlt} />)}
            {banners[2] && <PromoTile banner={banners[2]} large fallbackAlt={content.bannerAlt} />}
          </div>
        )}
      </div>
    </section>
  );
};

export default OffersSection;
